import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Copy, FileDown, FileSearch, FileText } from 'lucide-react';
import { detectDocumentRequest } from '@/lib/documentRequest';
import { EvaluationButtons } from './EvaluationButtons';
import { usePermissions } from '@/hooks/usePermissions';
import ParsedAssistantMessage from '@/components/Common/ParsedAssistantMessage';
import Modal from '@/components/Common/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Common/tabs';
import { cn } from '@/lib/utils';
import notify from '@/lib/notify';
import pdfService from '@/services/pdfService';
import { getAssistantAnswerText } from '@/lib/assistantMessageContent';
import { Message } from '@/services/conversations';
import { useIsSuperAdmin } from '@/stores/authStore';

type DownloadFormat = 'pdf' | 'md' | 'docx';

interface MessageItemProps {
  message: Message;
  conversationId?: string;
  previousUserMessage?: string;
}

type TraceValue = Record<string, unknown>;

const isPrimitiveValue = (value: unknown): boolean =>
  value === null || ['string', 'number', 'boolean'].includes(typeof value);

const formatTraceLabel = (key: string): string => {
  const withSpaces = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  if (!withSpaces) return key;
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

export const MessageItem: React.FC<MessageItemProps> = ({ 
  message, 
  conversationId,
  previousUserMessage 
}) => {
  const { t } = useTranslation();
  const [isTraceModalOpen, setIsTraceModalOpen] = useState(false);
  const [isDocumentContentExpanded, setIsDocumentContentExpanded] = useState(false);
  const isSuperAdmin = useIsSuperAdmin();
  const { canRead } = usePermissions();
  const messageSources = message.metadata?.sources || [];
  const debugTrace = (message.metadata?.debug_trace || null) as TraceValue | null;
  const hasDebugTrace = !message.is_user && isSuperAdmin && Boolean(debugTrace);
  const traceEntries = useMemo(() => Object.entries(debugTrace || {}), [debugTrace]);
  const generalTraceEntries = useMemo(
    () => traceEntries.filter(([, value]) => isPrimitiveValue(value)),
    [traceEntries]
  );
  const detailedTraceEntries = useMemo(
    () => traceEntries.filter(([, value]) => !isPrimitiveValue(value)),
    [traceEntries]
  );
  const traceJson = useMemo(
    () => (debugTrace ? JSON.stringify(debugTrace, null, 2) : ''),
    [debugTrace]
  );
  const buildMessageFilename = useCallback((content: string, messageId: string) => {
    const snippet = content
      .replace(/[#*_`>[()\]]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 40)
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .toLowerCase();

    const datePart = new Date().toISOString().slice(0, 10);
    return `chatbot_answer_${snippet || messageId}_${datePart}`;
  }, []);

  const displayAnswerText = getAssistantAnswerText(message.content);

  // When the reply is a downloadable document, render it as a download card
  // (client-side export) rather than dumping the text. The backend decides for
  // current replies (it also catches "en pdf" and tool-driven exports); older
  // replies without that metadata fall back to the client-side detector.
  const documentExport = message.metadata?.document_export;
  const documentRequestFormat = useMemo(() => {
    if (message.is_user) return null;
    if (documentExport !== undefined) return documentExport?.format ?? null;
    return detectDocumentRequest(previousUserMessage);
  }, [message.is_user, documentExport, previousUserMessage]);
  const documentTitle = useMemo(() => {
    const headingMatch = displayAnswerText.match(/^#{1,3}\s+(.+)$/m);
    const cleaned = (headingMatch?.[1] || '').replace(/[#*_`]/g, '').trim();
    return cleaned || t('aiChatbot.documentDefaultTitle');
  }, [displayAnswerText, t]);

  const handleCopyAsMarkdown = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(displayAnswerText);
      notify.success(t('aiChatbot.copySuccess'));
    } catch (error) {
      console.error('Error copying chatbot answer:', error);
      notify.error(t('aiChatbot.copyError'));
    }
  }, [displayAnswerText, t]);

  const handleDownloadMessage = useCallback(async (format: DownloadFormat) => {
    const filename = buildMessageFilename(displayAnswerText, message.id);

    try {
      if (format === 'pdf') {
        await pdfService.generatePDFFromMarkdown({
          markdownContent: displayAnswerText,
          filename,
          entityType: 'other',
        });
      } else if (format === 'docx') {
        await pdfService.downloadDocxFromMarkdown({
          markdownContent: displayAnswerText,
          filename,
          title: t('aiChatbot.downloadDocumentTitle'),
        });
      } else {
        pdfService.downloadMarkdown(displayAnswerText, filename);
      }

      notify.success(t('aiChatbot.downloadSuccess', { format: format.toUpperCase() }));
    } catch (error) {
      console.error(`Error downloading chatbot answer as ${format}:`, error);
      notify.error(t('aiChatbot.downloadError', { format: format.toUpperCase() }));
    }
  }, [buildMessageFilename, displayAnswerText, message.id, t]);

  const handleCopyText = useCallback(async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      notify.success(t('aiChatbot.copySuccessGeneric'));
    } catch (error) {
      console.error('Error copying trace content:', error);
      notify.error(t('aiChatbot.copyErrorGeneric'));
    }
  }, [t]);

  return (
    <div
      className={cn(
        'flex w-full',
        message.is_user ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={cn(
          message.is_user
            ? 'max-w-[min(92%,min(56rem,100%))] rounded-2xl border border-border/60 bg-muted/80 px-4 py-2.5 text-[15px] leading-relaxed text-foreground shadow-sm dark:bg-muted/50'
            : 'w-full max-w-none py-1 text-[15px] leading-relaxed'
        )}
      >
        {message.is_user ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : documentRequestFormat ? (
          <div className="space-y-3">
            <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <FileText className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground" title={documentTitle}>
                    {documentTitle}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('aiChatbot.documentReady', { format: documentRequestFormat.toUpperCase() })}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleDownloadMessage(documentRequestFormat)}
                className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <FileDown className="h-4 w-4" />
                {t('aiChatbot.downloadFormat', { format: `.${documentRequestFormat}` })}
              </button>
            </div>

            <button
              type="button"
              onClick={() => setIsDocumentContentExpanded((prev) => !prev)}
              className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
              aria-expanded={isDocumentContentExpanded}
            >
              {isDocumentContentExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              {isDocumentContentExpanded ? t('aiChatbot.hideContent') : t('aiChatbot.showContent')}
            </button>

            {isDocumentContentExpanded && (
              <ParsedAssistantMessage content={message.content} className="text-[15px] leading-relaxed" />
            )}
          </div>
        ) : (
          <ParsedAssistantMessage
            content={message.content}
            className="text-[15px] leading-relaxed"
            sources={messageSources}
          />
        )}

        {message.is_user && (message.attachments?.length ?? 0) > 0 && (
          <div className="mt-2 flex flex-wrap justify-end gap-1.5">
            {message.attachments!.map((attachment) => (
              <span
                key={attachment.id}
                className={cn(
                  'inline-flex max-w-[220px] items-center gap-1.5 rounded-md border border-border/60 bg-background/70 px-2 py-1 text-xs text-muted-foreground',
                  attachment.status === 'failed' && 'border-destructive/50 text-destructive'
                )}
                title={attachment.filename}
              >
                <FileText className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{attachment.filename}</span>
              </span>
            ))}
          </div>
        )}
        
        {!message.is_user && (
          <div className="mt-3 flex items-start justify-between gap-3 border-t border-border/40 pt-2">
            <div className="min-w-0 flex-1">
              {conversationId && previousUserMessage && canRead('evaluations') && (
                <EvaluationButtons
                  messageId={message.id}
                  conversationId={conversationId}
                  question={previousUserMessage}
                  response={displayAnswerText}
                  citations={messageSources}
                  existingEvaluation={message.evaluation}
                />
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {hasDebugTrace && (
                <button
                  type="button"
                  onClick={() => setIsTraceModalOpen(true)}
                  className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-border bg-background/70 px-3 text-xs text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
                  aria-label={t('aiChatbot.showTrace')}
                  title={t('aiChatbot.showTrace')}
                >
                  <FileSearch className="h-3.5 w-3.5" />
                  <span>{t('aiChatbot.showTrace')}</span>
                </button>
              )}
              {!documentRequestFormat && (
                <button
                  type="button"
                  onClick={() => void handleDownloadMessage('pdf')}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background/70 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
                  aria-label={t('aiChatbot.exportPdf')}
                  title={t('aiChatbot.exportPdf')}
                >
                  <FileDown className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => void handleCopyAsMarkdown()}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background/70 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
                aria-label={t('aiChatbot.copyAsMarkdown')}
                title={t('aiChatbot.copyAsMarkdown')}
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {hasDebugTrace && (
        <Modal
          open={isTraceModalOpen}
          onClose={() => setIsTraceModalOpen(false)}
          title={t('aiChatbot.traceTitle')}
          description={t('aiChatbot.traceDescription')}
          headerActions={
            <button
              type="button"
              onClick={() => void handleCopyText(traceJson)}
              className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-border bg-background/70 px-3 text-xs text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
              aria-label={t('aiChatbot.copyTraceJson')}
              title={t('aiChatbot.copyTraceJson')}
            >
              <Copy className="h-3.5 w-3.5" />
              <span>{t('aiChatbot.copyJson')}</span>
            </button>
          }
          className="max-w-5xl"
          bodyClassName="max-h-[70vh] overflow-y-auto"
        >
          <Tabs defaultValue="general" className="space-y-4">
            <div className="overflow-x-auto pb-1">
              <TabsList className="h-auto min-w-max items-stretch gap-1">
                <TabsTrigger value="general">{t('aiChatbot.traceGeneralTab')}</TabsTrigger>
                {detailedTraceEntries.map(([key]) => (
                  <TabsTrigger key={key} value={key}>
                    {formatTraceLabel(key)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value="general" className="mt-0">
              <div className="rounded-md border border-border bg-muted/40">
                {generalTraceEntries.length > 0 ? (
                  <div className="divide-y divide-border">
                    {generalTraceEntries.map(([key, value]) => (
                      <div key={key} className="grid grid-cols-[220px_minmax(0,1fr)] gap-4 px-4 py-3 text-sm">
                        <div className="font-medium text-foreground">{formatTraceLabel(key)}</div>
                        <div className="break-words text-muted-foreground">{String(value)}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-sm text-muted-foreground">
                    {t('aiChatbot.traceNoGeneralData')}
                  </div>
                )}
              </div>
            </TabsContent>

            {detailedTraceEntries.map(([key, value]) => (
              <TabsContent key={key} value={key} className="mt-0">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {formatTraceLabel(key)}
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleCopyText(JSON.stringify(value, null, 2))}
                    className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-border bg-background/70 px-3 text-xs text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
                    aria-label={t('aiChatbot.copyTraceSection', { section: formatTraceLabel(key) })}
                    title={t('aiChatbot.copyTraceSection', { section: formatTraceLabel(key) })}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    <span>{t('aiChatbot.copy')}</span>
                  </button>
                </div>
                <textarea
                  readOnly
                  value={JSON.stringify(value, null, 2)}
                  className="min-h-[240px] max-h-[52vh] w-full resize-y overflow-auto rounded-md border border-border bg-muted p-4 font-mono text-xs text-foreground focus:outline-none"
                  aria-label={formatTraceLabel(key)}
                />
              </TabsContent>
            ))}
          </Tabs>
        </Modal>
      )}
    </div>
  );
};

export type { Message };
